// Per-photo review on the store carousel (ADR-0169): the state badge every
// reviewed photo carries, the supervisor's decision controls in the
// lightbox footer, the annulled-observation line and the irreversible-
// annul confirmation.
//
// The wire carries codes only (packages/api-types/src/report-photos.ts);
// every label below is this dashboard's copy. The badge never touches the
// card's pill: that keeps reading the signed item verdict.
//
// Compliance shape (ADR-0169 §Cumplimiento): one photo per decision, two
// equal-weight buttons with no preselection (no aria-pressed, no initial
// focus, fixed order, no shortcuts), the AI proposal shown as a static
// hint, and an explicit acknowledgement before the auto-annul.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  photoReviewAnnulmentPreview,
  type PhotoReviewAnnulmentPreview,
  type PhotoReviewRequest,
  type ReportPhoto,
  type ReportPhotoGroup,
  type ReportPhotoReview,
} from "@bitacorapro/api-types";
import { useMemberships, useReviewPhotoMutation, type ReviewPhotoVars } from "../lib/api/queries";
import { ApiError } from "../lib/api/error";
import { useSession } from "../lib/auth/useSession";
import type { FlagTone } from "../lib/photoGroups";
import { fmtDate } from "../lib/util";
import { ConfirmDialog } from "./ConfirmDialog";
import { Icon } from "./icons";

/** GET /reports/:id/photos → `photoReview`, plus the report it is for. */
export type PhotoReviewContext = {
  reportId: string;
  enabled: boolean;
  canReview: boolean;
};

// ── State badge ──────────────────────────────────────────────────────

export type ReviewBadgeModel = {
  /** `.vgal__flag--*` modifier; "proposal" is the dashed AI hint. */
  tone: FlagTone | "proposal";
  label: string;
  /** Second, quieter part («IA: sin conclusión»). */
  secondary: string | null;
  icon: "warning" | "check" | null;
};

/**
 * One photo's review state. `defectTone` is the group's verdict severity
 * tone (photoGroups' rank mapping), so a confirmed problem reads with the
 * card's own severity.
 */
export function reviewBadge(review: ReportPhotoReview, defectTone: FlagTone): ReviewBadgeModel {
  const { confirmed, proposal } = review;
  if (review.phase === "confirmed" && confirmed) {
    return confirmed.verdict === "defecto"
      ? { tone: defectTone, label: "Problema confirmado", secondary: null, icon: "warning" }
      : // fitness-allow: postventa-labels-single-source — ADR-0169, the photo's own confirmed state, not a lifecycle label
        { tone: "ok", label: "Conforme", secondary: null, icon: "check" };
  }
  if (review.phase === "proposed" && proposal) {
    if (proposal.verdict === "defecto") {
      return {
        tone: "proposal",
        label: "Propuesta IA: problema",
        secondary: null,
        icon: "warning",
      };
    }
    if (proposal.verdict === "conforme") {
      return { tone: "proposal", label: "Propuesta IA: está bien", secondary: null, icon: "check" };
    }
    return { tone: "none", label: "Sin evaluar", secondary: "IA: sin conclusión", icon: null };
  }
  return { tone: "none", label: "Sin evaluar", secondary: null, icon: null };
}

/** «Sin evaluar. IA: sin conclusión» — the cell's accessible state. */
export function reviewBadgeText(b: ReviewBadgeModel): string {
  return b.secondary ? `${b.label}. ${b.secondary}` : b.label;
}

export function ReviewBadge({ badge, inCell }: { badge: ReviewBadgeModel; inCell: boolean }) {
  const Glyph =
    badge.icon === "warning" ? Icon.Warning : badge.icon === "check" ? Icon.Check : null;
  return (
    <span
      className={`vgal__flag vgal__flag--review vgal__flag--${badge.tone}${
        inCell ? "" : " vgal__flag--static"
      }`}
      // In a cell the button's aria-label already says it.
      aria-hidden={inCell ? "true" : undefined}
    >
      {Glyph && <Glyph s={12} />}
      <span className="vgal__flag-text">
        {badge.label}
        {badge.secondary && (
          <>
            <span aria-hidden="true"> · </span>
            <span className="sr-only">. </span>
            <span className="vgal__flag-sub">{badge.secondary}</span>
          </>
        )}
      </span>
    </span>
  );
}

// ── Pending photos ───────────────────────────────────────────────────

/** A displayed photo still waiting for a human decision. */
export function isPendingReview(p: ReportPhoto): boolean {
  return p.review !== null && p.review.phase !== "confirmed";
}

/** «Queda 1 por revisar.» / «Quedan 2 por revisar.» / none left. */
export function remainingText(k: number): string {
  if (k === 0) return "No quedan fotos por revisar.";
  return k === 1 ? "Queda 1 por revisar." : `Quedan ${k} por revisar.`;
}

// ── Names ────────────────────────────────────────────────────────────

/** actorUserId → display name, against the dashboard's own roster (the
 *  observation thread's contract, ADR-0085). Unknown → «un supervisor». */
export function useMemberName(): (userId: string) => string {
  const q = useMemberships();
  const byId = useMemo(
    () => new Map((q.data?.members ?? []).map((m) => [m.userId, m.displayName])),
    [q.data]
  );
  return (userId) => byId.get(userId) ?? "un supervisor";
}

export function AnnulledLine({
  group,
  focusOnMount = false,
  onFocused,
}: {
  group: ReportPhotoGroup;
  focusOnMount?: boolean;
  /** Called once the line took focus, so the caller can drop the flag:
   *  a later remount (←/→ inside the annulled card) must not pull focus
   *  off the navigation buttons. */
  onFocused?: () => void;
}) {
  const nameOf = useMemberName();
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!focusOnMount || !ref.current) return;
    ref.current.focus();
    onFocused?.();
  }, [focusOnMount, onFocused]);
  if (!group.annulment) return null;
  return (
    // tabIndex stays -1 (never in the tab order): dropping it while the
    // line holds focus would send focus to <body>.
    <p className="vgal__note vgal__annulled" ref={ref} tabIndex={-1}>
      Anulada el {fmtDate(group.annulment.at)} por {nameOf(group.annulment.actorUserId)}
    </p>
  );
}

/**
 * A review outcome that outlives the decision controls (they unmount when
 * the refetch makes the item non-reviewable or annulled), shown in the
 * lightbox footer by the gallery. Takes focus once when `focusOnMount`,
 * so focus never falls to <body> with the button that was pressed.
 */
export function ReviewNotice({
  text,
  focusOnMount,
  onFocused,
}: {
  text: string;
  focusOnMount: boolean;
  onFocused: () => void;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!focusOnMount || !ref.current) return;
    ref.current.focus();
    onFocused();
  }, [focusOnMount, onFocused]);
  return (
    <p className="vlb__alert" ref={ref} tabIndex={-1}>
      {text}
    </p>
  );
}

// ── Annul confirmation ───────────────────────────────────────────────

export type AnnulRequest = {
  itemLabel: string;
  preview: PhotoReviewAnnulmentPreview | null;
  /** Sends the pending «Está bien» with acknowledgeAnnulment: true. */
  confirm: () => void;
};

export function ConfirmAnnulDialog({
  request,
  onClose,
}: {
  request: AnnulRequest | null;
  onClose: () => void;
}) {
  const target = request?.preview?.toStatusLabel ?? "Anulada";
  return (
    <ConfirmDialog
      open={request !== null}
      title="¿Anular esta observación?"
      description={
        request
          ? `Todas las fotos de «${request.itemLabel}» quedarían como conformes. La observación pasará a «${target}»: se desactiva el enlace del contratista y se avisa al jefe de tienda. Esto no se puede deshacer. El acta firmada no cambia.`
          : ""
      }
      confirmLabel="Anular observación"
      onCancel={onClose}
      onConfirm={() => {
        const confirm = request?.confirm;
        onClose();
        confirm?.();
      }}
    />
  );
}

// ── Decision controls (lightbox footer) ──────────────────────────────

export type ConfirmedPhotoVerdict = PhotoReviewRequest["verdict"];

export const DECISION_TEXT: Record<ConfirmedPhotoVerdict, string> = {
  defecto: "muestra el problema",
  conforme: "está bien",
};

function proposalText(review: ReportPhotoReview | null): string | null {
  switch (review?.proposal?.verdict) {
    case "defecto":
      return "La IA propone: muestra un problema.";
    case "conforme":
      return "La IA propone: está bien.";
    case "no_concluyente":
      return "La IA no pudo concluir con esta foto.";
    default:
      return null;
  }
}

// Only an unknown outcome is worth a retry: the network failed, the
// server failed (5xx) or throttled (429). Every other 4xx is a stable
// answer — the idempotency plugin drops the key on a non-2xx, so a retry
// would run the same checks and fail the same way.
function isRetryable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status === 0 || err.status === 429 || err.status >= 500 || err.status < 400;
}

/** The copy for a stable (non-retryable) refusal of a photo decision. */
export function reviewRefusalText(code: string): string {
  switch (code) {
    case "CONFIRMATION_STALE":
      return "La sugerencia de la IA para esta foto cambió. Revísala de nuevo antes de decidir.";
    case "PHOTO_REVIEW_LOCKED_ANNULLED":
      return "Esta observación ya fue anulada. Actualizamos lo que ves.";
    case "PHOTO_REVIEW_FORBIDDEN":
    case "ROLE_READ_ONLY":
    case "SUPPORT_ACCESS_READ_ONLY":
      return "No tienes permiso para revisar esta foto.";
    case "PHOTO_NOT_DISPLAYED":
    case "PHOTO_NOT_IN_ITEM":
    case "RESULT_NOT_FOUND":
      return "Esta foto ya no está entre las que se revisan. Actualizamos lo que ves.";
    case "REPORT_SIGNED_WITHOUT_SNAPSHOT":
      return "Esta observación no se puede anular desde la revisión de fotos.";
    case "PHOTO_REVIEW_ITEM_NOT_REVIEWABLE":
    case "PHOTO_REVIEW_DISABLED":
    case "PHOTO_REVIEW_NOT_ENABLED":
    case "PHOTO_REVIEW_REQUIRES_TERMINAL_REPORT":
    case "REPORT_NOT_FOUND":
      return "Esta observación ya no admite revisión. Actualizamos lo que ves.";
    default:
      return "No pudimos guardar tu decisión y no sirve reintentarlo. La foto sigue sin confirmar.";
  }
}

type Failure = { kind: "error"; retry: ReviewPhotoVars };

export type PhotoReviewControlsProps = {
  reportId: string;
  group: ReportPhotoGroup;
  /** The card's displayed photos, in order. */
  photos: readonly ReportPhoto[];
  photo: ReportPhoto;
  itemLabel: string;
  /** Focus the group label once mounted («Siguiente por revisar»). */
  focusLabel: boolean;
  onFocusLabelDone: () => void;
  onDecided: (verdict: ConfirmedPhotoVerdict, annulled: boolean) => void;
  onRequestAnnul: (req: AnnulRequest) => void;
  /** A stable refusal (any 4xx but the annul ack): the gallery shows it
   *  where it survives these controls unmounting after the refetch. */
  onRefused: (text: string) => void;
};

export function PhotoReviewControls({
  reportId,
  group,
  photos,
  photo,
  itemLabel,
  focusLabel,
  onFocusLabelDone,
  onDecided,
  onRequestAnnul,
  onRefused,
}: PhotoReviewControlsProps) {
  // One mutation instance per photo: the footer is keyed by photo, so a
  // pending decision never disables another photo's buttons.
  const mutation = useReviewPhotoMutation(reportId);
  const session = useSession();
  const nameOf = useMemberName();
  const labelId = useId();
  const hintId = useId();
  const labelRef = useRef<HTMLParagraphElement>(null);
  const changeRef = useRef<HTMLButtonElement>(null);
  const inFlight = useRef(false);
  const [editing, setEditing] = useState(false);
  const [focusChange, setFocusChange] = useState(false);
  const [focusLabelLocal, setFocusLabelLocal] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  const review = photo.review;
  const confirmed = review?.phase === "confirmed" ? review.confirmed : null;
  const showDecided = confirmed !== null && !editing;
  const hint = proposalText(review);

  useEffect(() => {
    if (!focusLabel) return;
    labelRef.current?.focus();
    onFocusLabelDone();
  }, [focusLabel, onFocusLabelDone]);

  useEffect(() => {
    if (focusLabelLocal) {
      labelRef.current?.focus();
      setFocusLabelLocal(false);
    }
  }, [focusLabelLocal]);

  useEffect(() => {
    // After a decision lands, focus moves to «Cambiar decisión» — never
    // left on a button that just disappeared (which drops it to <body>).
    if (focusChange && showDecided) {
      changeRef.current?.focus();
      setFocusChange(false);
    }
  }, [focusChange, showDecided]);

  function send(vars: ReviewPhotoVars) {
    if (inFlight.current) return;
    inFlight.current = true;
    setFailure(null);
    mutation.mutate(vars, {
      onSuccess: (data) => {
        setEditing(false);
        if (!data.annulled) setFocusChange(true);
        onDecided(vars.body.verdict, data.annulled);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code === "ANNULMENT_ACK_REQUIRED") {
          const parsed = photoReviewAnnulmentPreview.safeParse(err.details?.annulmentPreview);
          requestAnnul(parsed.success ? parsed.data : null);
          return;
        }
        if (isRetryable(err)) {
          setFailure({ kind: "error", retry: vars });
          return;
        }
        // The hook's onSettled already refetches the photos.
        setEditing(false);
        onRefused(reviewRefusalText(err instanceof ApiError ? err.code : "UNKNOWN"));
      },
      onSettled: () => {
        inFlight.current = false;
      },
    });
  }

  function varsFor(verdict: ConfirmedPhotoVerdict, acknowledgeAnnulment: boolean): ReviewPhotoVars {
    return {
      body: {
        resultId: group.id,
        photoUrl: photo.url,
        verdict,
        seenProposalRunId: review?.proposal?.runId ?? null,
        acknowledgeAnnulment,
      },
      // A fresh key per user decision; «Reintentar» replays this one.
      idempotencyKey: crypto.randomUUID(),
      findingId: group.observation?.findingId ?? null,
    };
  }

  function requestAnnul(preview: PhotoReviewAnnulmentPreview | null) {
    onRequestAnnul({
      itemLabel,
      preview,
      // Confirming in the dialog is its own decision: a new key.
      confirm: () => send(varsFor("conforme", true)),
    });
  }

  // Every OTHER displayed photo already confirmed «conforme» — so this
  // «Está bien» is the one the server says would annul.
  const isLastForAnnul =
    group.wouldAnnulOnNextConforme &&
    photos.every((p) => p.id === photo.id || p.review?.confirmed?.verdict === "conforme");

  function decide(verdict: ConfirmedPhotoVerdict) {
    if (inFlight.current) return;
    if (verdict === "conforme" && isLastForAnnul) {
      requestAnnul(null);
      return;
    }
    send(varsFor(verdict, false));
  }

  const pending = mutation.isPending;
  const self = confirmed && session?.user.id === confirmed.actorUserId;

  return (
    <div
      className="vlb__decide"
      role="group"
      aria-labelledby={labelId}
      aria-describedby={hint ? hintId : undefined}
    >
      <p className="vlb__decide-label" id={labelId} ref={labelRef} tabIndex={-1}>
        ¿Esta foto muestra el problema?
      </p>
      {hint && (
        <p className="vlb__hint" id={hintId}>
          {hint} Es una sugerencia. Tú decides mirando la foto.
        </p>
      )}
      {showDecided && confirmed ? (
        <div className="vlb__decided">
          <p className="vlb__decision">
            {self ? "Tu decisión" : "Decisión"}: {DECISION_TEXT[confirmed.verdict]}
          </p>
          <p className="vlb__decision-by">
            Confirmada por {nameOf(confirmed.actorUserId)} el {fmtDate(confirmed.at)}
          </p>
          <button
            ref={changeRef}
            type="button"
            className="vlb__btn vlb__btn--secondary"
            onClick={() => {
              setEditing(true);
              setFailure(null);
              setFocusLabelLocal(true);
            }}
          >
            Cambiar decisión
          </button>
        </div>
      ) : (
        <div className="vlb__actions">
          <button
            type="button"
            className="vlb__btn vlb__btn--primary"
            disabled={pending}
            onClick={() => decide("defecto")}
          >
            Muestra el problema
          </button>
          <button
            type="button"
            className="vlb__btn vlb__btn--primary"
            disabled={pending}
            onClick={() => decide("conforme")}
          >
            Está bien
          </button>
        </div>
      )}
      {failure?.kind === "error" && (
        <div className="vlb__alert" role="alert">
          <span>No pudimos guardar tu decisión. La foto sigue sin confirmar.</span>
          <button
            type="button"
            className="vlb__btn vlb__btn--secondary"
            disabled={pending}
            onClick={() => send(failure.retry)}
          >
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
}
